<template>
  <div v-if="showPlatformList">
    <AuBreadcrumb :parent-name="$t('router.pages.dashboard')" />
    <Form
      padding="20px"
      :is-bordered="false"
    >
      <template #title>
        {{ $t("myPlatforms.title") }}
      </template>
      <template #subtitle>
        <div class="items-count">
          <span>
            {{ $t("myPlatforms.subtitle") }}:
          </span>
          <AuSkeleton v-if="loading" />
          <span v-else>
            {{ items?.length }}
          </span>
        </div>
      </template>
      <template #header-right>
        <div class="btn-place">
          <AuButton @click="goToAPIDoc">
            {{ $t("myPlatforms.apiButton") }}
          </AuButton>
          <AuButton
            type="primary"
            @click="goToNewPlatform"
          >
            {{ $t("myPlatforms.createButton") }}
          </AuButton>
        </div>
      </template>

      <ProgressLoader
        v-if="loading"
        mode="indeterminate"
        color="#5AC396"
        size="150px"
      />

      <div v-else>
        <!-- Заменяем AuTable на UTable -->
        <UTable
          class="platforms-table"
          :class="{ 'briefcase-table-hiddable': true }"
          :items="items"
          :columns="tableColumns"
        >
          <!-- Слот для названия платформы -->
          <template #name="{ value }">
            {{ value }}
          </template>

          <!-- Слот для API ключа -->
          <template #api="{ value }">
            <SpinnerLoader
              v-if="itemsLoading[value.index]"
              color="#5AC396"
              size="30px"
            />
            <div v-else>
              <div
                v-if="value.key"
                class="key-place"
              >
                {{ value.key }}
              </div>
              <AuIcon
                v-else
                icon="refresh"
                clickable
                @click="getAPIKey(value.id, value.index)"
              />
            </div>
          </template>

          <!-- Слот для домена -->
          <template #url="{ value }">
            {{ value }}
          </template>

          <!-- Слот для webhook -->
          <template #callbackInvestment="{ value }">
            {{ value }}
          </template>

          <!-- Слот для статуса -->
          <template #status="{ value }">
            {{ value }}
          </template>

          <!-- Слот для действий - пустой, так как используется type="menu" -->
          <template #actions>
            <!-- Пустой слот, рендеринг через menuFunction -->
          </template>
        </UTable>

        <AuPagination
          v-if="totalItems > itemsPerPage"
          :total-items="totalItems"
          @pageChanged="changePage"
        />
      </div>
    </Form>

    <!-- Модальное окно подтверждения действий -->
    <AuModal
      v-model="showModal"
      width="500px"
    >
      <template #header>
        <span class="title indent">
          {{ modalTitle }}
        </span>
      </template>
      <template #body>
        <div class="indent">
          {{ modalBody }}
        </div>
      </template>
      <template #footer>
        <div class="btn-place btn-end">
          <AuButton @click="doAction">
            {{ modalButton }}
          </AuButton>
          <AuButton @click="showModal = false">
            {{ $t("myPlatforms.cancel") }}
          </AuButton>
        </div>
      </template>
    </AuModal>
  </div>
  <RouterView
    v-else
    @add="addPlatform"
    @edit="patchPlatform"
  />
</template>

<script>
import _ from "lodash";
import ProgressLoader from "@/loaders/progress-bar";
import SpinnerLoader from "@/loaders/progress-spinner";
import UTable from "@/components/uikit/uComponents/UTable.vue";
import { mapActions } from "vuex";

export default {
  name: "MyPlatforms",
  components: {
    ProgressLoader,
    SpinnerLoader,
    UTable
  },
  data() {
    return {
      loading: true,
      platforms: [],
      totalItems: 0,
      page: 1,
      itemsPerPage: 30,
      itemsLoading: [],
      showModal: false,
      domain: "",
      file: "",
      name: "",
      modalId: null,
      mode: null
    };
  },

  computed: {
    showPlatformList() {
      return this.$route.name === "my platforms";
    },

    // Колонки для UTable
    tableColumns() {
      return [
        {
          property: "name",
          header: this.$t("myPlatforms.name"),
          align: "start"
        },
        {
          property: "api",
          header: this.$t("myPlatforms.key"),
          align: "start"
        },
        {
          property: "url",
          header: this.$t("myPlatforms.domain"),
          align: "start"
        },
        {
          property: "callbackInvestment",
          header: this.$t("myPlatforms.webhook"),
          align: "center"
        },
        {
          property: "status",
          header: this.$t("myPlatforms.status"),
          align: "center"
        },
        {
          property: "actions",
          header: this.$t("myPlatforms.actions"),
          align: "center",
          type: "menu",
          menuFunction: this.getPlatformMenuItems
        }
      ];
    },

    // Форматирование данных для таблицы
    items() {
      return _.map(this.platforms, (item, index) => {
        // Создаем уникальный ID
        const platformId = item.id || `platform-${index}`;

        return {
          id: platformId,
          name: item?.name ?? "-",
          api: {
            id: item.id,
            index: index,
            key: null,
            loading: false
          },
          url: item?.url ?? "-",
          callbackInvestment: item?.callbackInvestment ?? "-",
          status: item?.status ?? "-",
          // Данные для контекстного меню
          actionData: {
            id: item.id,
            index: index,
            status: item.status,
            name: item.name,
            url: item.url,
            uuid: item.uuid
          }
        };
      });
    },

    modalTitle() {
      return this.mode === "freeze" ? this.$t("myPlatforms.modalFreeze") :
        (this.mode === "unfreeze" ? this.$t("myPlatforms.modalUnfreeze") : this.$t("myPlatforms.modalConfirm"));
    },

    modalBody() {
      return this.mode === "freeze" ?
        this.$t("myPlatforms.pleaseNote") :
        (this.mode === "unfreeze" ? this.$t("myPlatforms.unfreezePlatform", { name: this.name }) :
          this.$t("myPlatforms.placeTheFile", { domain: this.domain, file: this.file }));
    },

    modalButton() {
      return this.mode === "freeze" ? this.$t("myPlatforms.freeze") :
        (this.mode === "unfreeze" ? this.$t("myPlatforms.unfreeze") : this.$t("myPlatforms.check"));
    }
  },

  async mounted() {
    await this.getPlatformsData();
    this.disableItemLoading();
  },

  methods: {
    ...mapActions({
      getPlatforms: "platforms/getPlatforms",
      createPlatform: "platforms/createPlatform",
      editPlatform: "platforms/editPlatform",
      freezePlatform: "platforms/freezePlatform",
      verifyPlatform: "platforms/verifyPlatform",
      getPlatformKey: "platforms/getPlatformKey"
    }),

    // Функция для генерации пунктов контекстного меню
    getPlatformMenuItems(actionData) {
      const items = [];

      // Редактирование
      items.push({
        label: this.$t("myPlatforms.edit"),
        command: () => {
          this.goToEditPlatform(actionData.id);
        }
      });

      // Подтверждение (только для черновиков)
      if (actionData.status === "draft") {
        items.push({
          label: this.$t("myPlatforms.confirm"),
          command: () => {
            this.openModal("verify", actionData.id, actionData.index);
          }
        });
      }

      // Заморозка/разморозка
      items.push({
        label: actionData.status === "freeze" ? this.$t("myPlatforms.unfreeze") : this.$t("myPlatforms.freeze"),
        command: () => {
          const mode = actionData.status === "freeze" ? "unfreeze" : "freeze";
          this.openModal(mode, actionData.id, actionData.index);
        }
      });

      return [
        {
          items: items
        }
      ];
    },

    async getPlatformsData() {
      this.loading = true;
      const params = {
        page: this.page,
        itemsPerPage: this.itemsPerPage
      };
      try {
        const resp = await this.getPlatforms(params);
        this.platforms = resp.data;
        this.totalItems = resp.totalItems;
      }
      catch (error) {
        this.$toast.error(
          error.response.data?.message || error.response.data?.detail
        );
      }
      this.loading = false;
    },

    async changePage(page) {
      this.page = page;
      await this.getPlatformsData();
    },

    disableItemLoading() {
      this.itemsLoading = _.map(this.platforms, () => false);
    },

    goToAPIDoc() {
      this.$router.push({ name: "api doc" });
    },

    goToNewPlatform() {
      this.$router.push({ name: "new platform" });
    },

    goToEditPlatform(id) {
      this.$router.push({ name: "new platform", params: { id: id } });
    },

    openModal(mode, id, index) {
      this.mode = mode;
      this.modalId = id;
      this.name = this.platforms[index].name;
      this.domain = this.platforms[index].url;
      this.file = this.platforms[index].uuid + ".txt";
      this.showModal = true;
    },

    doAction() {
      this.showModal = false;
      if (this.mode === "verify") {
        this.confirmPlatform();
      }
      else {
        this.blockPlatform();
      }
    },

    prepareParams(platform) {
      const ret = {
        name: platform.name,
        url: platform.url,
        description: platform.description,
        callbackInvestment: platform.callbackInvestment
      };
      if (platform?.id) {
        ret.id = platform.id;
      }
      return ret;
    },

    async addPlatform(platform) {
      const params = this.prepareParams(platform);
      params.user = `/api/users/${this.$auth.user().id}`;
      try {
        await this.createPlatform(params);
        await this.getPlatformsData();
      }
      catch (error) {
        this.$toast.error(
          error.response.data?.message || error.response.data?.detail
        );
      }
    },

    async patchPlatform(platform) {
      const params = this.prepareParams(platform);
      try {
        await this.editPlatform(params);
        await this.getPlatformsData();
      }
      catch (error) {
        this.$toast.error(
          error.response.data?.message || error.response.data?.detail
        );
      }
    },

    async blockPlatform() {
      try {
        const resp = await this.freezePlatform(this.modalId);
        if (resp.status === 267) {
          this.$toast.error(resp.data.error);
        }
        else {
          await this.getPlatformsData();
        }
      }
      catch (error) {
        this.$toast.error(
          error.response.data?.message || error.response.data?.detail
        );
      }
    },

    async confirmPlatform() {
      try {
        const resp = await this.verifyPlatform(this.modalId);
        if (resp.status === 267) {
          this.$toast.error(resp.data.error);
        }
        else {
          await this.getPlatformsData();
        }
      }
      catch (error) {
        this.$toast.error(
          error.response.data?.message || error.response.data?.detail
        );
      }
    },

    async getAPIKey(id, index) {
      this.itemsLoading[index] = true;
      try {
        const resp = await this.getPlatformKey(id);
        if (resp.status === 267) {
          this.$toast.error(resp.data.error);
        }
        else {
          // Обновляем данные в items через массив platforms
          this.$set(this.platforms[index], "apiKey", resp.data?.key);
          // Обновляем отображение
          this.$forceUpdate();
        }
      }
      catch (error) {
        this.$toast.error(
          error.response.data?.message || error.response.data?.detail
        );
      }
      this.itemsLoading[index] = false;
    }
  }
};
</script>

<style scoped lang="scss">
.btn-place {
  display: flex;
  gap: 10px;
}
.btn-end {
  justify-content: end;
}
.indent {
  margin-bottom: 20px;
}
.title {
  font-weight: 450;
  font-size: 20px;
}
.key-place {
  width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.items-count {
  display: flex;
  align-items: center;
  gap: 5px;

  span {
    font-weight: 450;
    font-size: 12px;
    color: var(--color-60);
  }
}

// Стили для мобильной версии
.platforms-table {
  @media screen and (max-width:670px) {
    display: none;
  }
}

// Можно добавить аккордеон для мобильной версии, если нужно
.platforms-accordion {
  display: none;

  @media screen and (max-width:670px) {
    display: block;
  }
}
</style>